// src/pages/customers/tabs/CustomerStats.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../../../../utils/config";

/* ------------------ utils ------------------ */
const toISO = (d) => {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${yyyy}-${mm}-${dd}`;
};
const money = (n) =>
  isFinite(Number(n))
    ? Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "0.00";
const pct = (n) =>
  isFinite(Number(n)) ? (Number(n) * 100).toFixed(1) + "%" : "—";
const daysBetween = (d) =>
  d ? Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86400000)) : "—";

// Current FY (India): Apr 1 – Mar 31
function currentFYRange() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0=Jan
  const startYear = m >= 3 ? y : y - 1;
  const start = new Date(startYear, 3, 1);
  const end = new Date(startYear + 1, 2, 31);
  return { from: toISO(start), to: toISO(end) };
}

/* ------------------ charts (SVG, no deps) ------------------ */
function LineChart({ points, height = 120, strokeWidth = 2, className = "" }) {
  if (!points || points.length === 0)
    return <div className="text-xs text-slate-500">No data</div>;

  const w = Math.max(240, points.length * 56);
  const xs = points.map((_, i) => (i / Math.max(1, points.length - 1)) * (w - 16) + 8);

  const vals = points.map((p) => Number(p.value) || 0);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const ys = points.map((p) => {
    const t = ((Number(p.value) || 0) - min) / range;
    return height - 16 - t * (height - 32);
  });

  const path = xs.map((x, i) => `${i === 0 ? "M" : "L"} ${x},${ys[i]}`).join(" ");
  const area = `M ${xs[0]},${height - 16} ` + xs.map((x, i) => `L ${x},${ys[i]}`).join(" ") + ` L ${xs.at(-1)},${height - 16} Z`;

  return (
    <svg viewBox={`0 0 ${w} ${height}`} className={`w-full ${className}`}>
      <path d={area} fill="currentColor" opacity="0.12" />
      <path d={path} fill="none" stroke="currentColor" strokeWidth={strokeWidth} />
    </svg>
  );
}

function BarChart({ items, height = 140, className = "" }) {
  if (!items || items.length === 0)
    return <div className="text-xs text-slate-500">No data</div>;
  const max = Math.max(...items.map((x) => Number(x.value) || 0)) || 1;
  const barW = 40, gap = 16;
  const w = items.length * (barW + gap) + gap;
  return (
    <svg viewBox={`0 0 ${w} ${height}`} className={`w-full ${className}`}>
      {items.map((it, i) => {
        const v = Number(it.value) || 0;
        const h = Math.max(2, (v / max) * (height - 30));
        const x = gap + i * (barW + gap);
        const y = height - h - 16;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={h} rx="6" className="fill-indigo-500/80" />
            <text x={x + barW / 2} y={height - 4} fontSize="10" textAnchor="middle" className="fill-slate-600">
              {it.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* ------------------ page ------------------ */
export default function CustomerStats() {
  const { id, customerId: id2 } = useParams();
  const customerId = id || id2;
  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Default range = Current FY
  const fy = useMemo(currentFYRange, []);
  const [from, setFrom] = useState(fy.from);
  const [to, setTo] = useState(fy.to);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState({
    summary: null,
    trend: [],
    topProducts: [],
    recentInvoices: [],
    recentVisits: [],
  });

  async function fetchData() {
    if (!customerId) return;
    setLoading(true);
    setErr("");
    try {
      const endpoints = [
        `${API_BASE_URL}/api/stats/customers/${customerId}`,
        `${API_BASE_URL}/stats/customers/${customerId}`,
      ];
      let res = null, lastErr = null;
      for (const url of endpoints) {
        try {
          res = await axios.get(url, { params: { from, to }, headers });
          break;
        } catch (e) {
          lastErr = e;
          if (e?.response?.status === 404) continue;
        }
      }
      if (!res) throw lastErr || new Error("No stats endpoint");

      const payload = res.data || {};
      setData({
        summary: payload.summary || null,
        trend: Array.isArray(payload.trend) ? payload.trend : [],
        topProducts: Array.isArray(payload.topProducts) ? payload.topProducts : [],
        recentInvoices: Array.isArray(payload.recentInvoices) ? payload.recentInvoices : [],
        recentVisits: Array.isArray(payload.recentVisits) ? payload.recentVisits : [],
      });
    } catch (e) {
      console.error(e);
      setErr("Failed to load stats.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [customerId, from, to]);

  // Derive profit & margin safely from summary if present
  const s = data.summary || {};
  const gross = Number(s.grossSales ?? s.netSales ?? 0);
  const cogs = Number.isFinite(Number(s.cogs ?? s.costOfGoods ?? s.cogsTotal))
    ? Number(s.cogs ?? s.costOfGoods ?? s.cogsTotal)
    : null;
  const profit = Number.isFinite(cogs) ? gross - cogs : (Number.isFinite(Number(s.profit)) ? Number(s.profit) : null);
  const margin = profit != null && gross > 0 ? profit / gross : (Number.isFinite(Number(s.margin)) ? Number(s.margin) : null);

  const kpis = [
    { label: "Net Sales", value: "₹ " + money(s.netSales ?? gross), color: "from-emerald-500 to-teal-500" },
    { label: "Orders", value: s.orders ?? 0, color: "from-indigo-500 to-blue-500" },
    { label: "AOV", value: "₹ " + money(s.aov), color: "from-sky-500 to-cyan-500" },
    { label: "Outstanding", value: "₹ " + money(s.outstanding), color: "from-rose-500 to-pink-500" },
    { label: "Profit", value: profit != null ? "₹ " + money(profit) : "—", color: "from-amber-500 to-orange-500" },
    { label: "Margin", value: margin != null ? pct(margin) : "—", color: "from-fuchsia-500 to-purple-500" },
    { label: "Visit→Order", value: pct((s.visits || 0) ? (s.ordersSameDay || 0) / s.visits : 0), color: "from-lime-500 to-green-500" },
    { label: "Days Since Order", value: daysBetween(s.lastOrder), color: "from-slate-500 to-slate-400" },
  ];

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header + filter */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Customer Performance</h2>
          <div className="text-xs text-slate-500">Range: {from} → {to}</div>
        </div>

        {/* Filter bar */}
        <div className="bg-white rounded-xl shadow p-3 flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <div className="flex flex-col">
            <label className="text-[11px] text-slate-500 mb-1">From</label>
            <input
              type="date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-[11px] text-slate-500 mb-1">To</label>
            <input
              type="date"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={fetchData}
              className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700"
            >
              Refresh
            </button>
            {/* Quick presets */}
            <div className="flex gap-2">
              <button
                onClick={() => { const { from:f, to:t } = currentFYRange(); setFrom(f); setTo(t); }}
                className="px-2 py-1.5 rounded-lg text-xs bg-slate-100 hover:bg-slate-200"
              >
                Current FY
              </button>
              <button
                onClick={() => { const d = new Date(); const f = new Date(); f.setDate(d.getDate() - 90); setFrom(toISO(f)); setTo(toISO(d)); }}
                className="px-2 py-1.5 rounded-lg text-xs bg-slate-100 hover:bg-slate-200"
              >
                90D
              </button>
              <button
                onClick={() => { const d = new Date(); const f = new Date(d.getFullYear(), d.getMonth(), 1); setFrom(toISO(f)); setTo(toISO(d)); }}
                className="px-2 py-1.5 rounded-lg text-xs bg-slate-100 hover:bg-slate-200"
              >
                MTD
              </button>
              <button
                onClick={() => { const d = new Date(); const f = new Date(d.getFullYear(), 0, 1); setFrom(toISO(f)); setTo(toISO(d)); }}
                className="px-2 py-1.5 rounded-lg text-xs bg-slate-100 hover:bg-slate-200"
              >
                YTD
              </button>
            </div>
          </div>
        </div>
      </div>

      {err && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</div>}
      {loading && <div className="rounded-lg border bg-white px-3 py-2 text-sm text-slate-600">Loading…</div>}

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-3">
        {kpis.map((k) => (
          <div
            key={k.label}
            className="rounded-2xl overflow-hidden shadow-sm border bg-white"
          >
            <div className={`h-1 w-full bg-gradient-to-r ${k.color}`} />
            <div className="p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">{k.label}</div>
              <div className="text-lg font-semibold text-slate-900">{k.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Trend: Sales (and Profit if present) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border bg-white p-4">
          <div className="mb-2 font-semibold text-slate-900">Monthly Net Sales</div>
          <LineChart
            className="text-emerald-600"
            points={(data.trend || []).map((t) => ({
              label: t.month,
              value: Number(t.netSales ?? t.grossSales ?? 0),
            }))}
          />
          <div className="mt-1 text-[11px] text-slate-500">
            {(data.trend || [])
              .map((t) => new Date(t.month).toLocaleDateString("en-GB", { month: "short", year: "2-digit" }))
              .join(" · ")}
          </div>
        </div>

        <div className="rounded-2xl border bg-white p-4">
          <div className="mb-2 font-semibold text-slate-900">Monthly Profit</div>
          <LineChart
            className="text-amber-600"
            points={(data.trend || []).map((t) => {
              const gs = Number(t.netSales ?? t.grossSales ?? 0);
              const c = Number.isFinite(Number(t.cogs)) ? Number(t.cogs) : null;
              const p = c != null ? gs - c : Number(t.profit ?? NaN);
              return { label: t.month, value: Number.isFinite(p) ? p : 0 };
            })}
          />
          <div className="mt-1 text-[11px] text-slate-500">
            {(data.trend || [])
              .map((t) => new Date(t.month).toLocaleDateString("en-GB", { month: "short", year: "2-digit" }))
              .join(" · ")}
          </div>
        </div>
      </div>

      {/* Top Products */}
      <div className="rounded-2xl border bg-white p-4">
        <div className="mb-2 font-semibold text-slate-900">Top Products</div>
        <BarChart
          className="text-indigo-600"
          items={(data.topProducts || []).map((p) => ({
            label: (p.name || p.productName || "").toString().slice(0, 6),
            value: Number(p.revenue || 0),
          }))}
        />
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="px-3 py-2 text-left">Product</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2 text-right">Revenue</th>
                <th className="px-3 py-2 text-right">Profit</th>
              </tr>
            </thead>
            <tbody>
              {(data.topProducts || []).map((p) => {
                const rev = Number(p.revenue || 0);
                const c = Number.isFinite(Number(p.cogs)) ? Number(p.cogs) : null;
                const pr = c != null ? rev - c : Number(p.profit ?? NaN);
                return (
                  <tr key={p.product_id || p.name} className="border-t">
                    <td className="px-3 py-2">{p.name || p.productName || p.product_id}</td>
                    <td className="px-3 py-2 text-right">{p.qty ?? 0}</td>
                    <td className="px-3 py-2 text-right">₹ {money(rev)}</td>
                    <td className="px-3 py-2 text-right">{Number.isFinite(pr) ? `₹ ${money(pr)}` : "—"}</td>
                  </tr>
                );
              })}
              {(data.topProducts || []).length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-2 text-slate-500">No data</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Invoices & Visits */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Invoices */}
        <div className="rounded-2xl border bg-white p-4">
          <div className="mb-2 font-semibold text-slate-900">Recent Invoices</div>
          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {(data.recentInvoices || []).map((i) => (
              <div key={i.id} className="rounded-xl border p-3">
                <div className="flex items-center justify-between">
                  <div className="font-medium">Invoice #{i.id}</div>
                  <div className="text-xs text-slate-500">
                    {new Date(i.invoice_date).toLocaleDateString("en-GB")}
                  </div>
                </div>
                <div className="mt-1 text-emerald-700 font-semibold">₹ {money(i.total_amount)}</div>
              </div>
            ))}
            {(data.recentInvoices || []).length === 0 && (
              <div className="rounded-xl border p-3 text-slate-500">No invoices</div>
            )}
          </div>
          {/* Desktop table */}
          <table className="hidden md:table min-w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(data.recentInvoices || []).map((i) => (
                <tr key={i.id} className="border-t">
                  <td className="px-3 py-2">
                    {new Date(i.invoice_date).toLocaleDateString("en-GB")}
                  </td>
                  <td className="px-3 py-2 text-right text-emerald-700 font-semibold">
                    ₹ {money(i.total_amount)}
                  </td>
                </tr>
              ))}
              {(data.recentInvoices || []).length === 0 && (
                <tr><td colSpan={2} className="px-3 py-2 text-slate-500">No invoices</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Visits */}
        <div className="rounded-2xl border bg-white p-4">
          <div className="mb-2 font-semibold text-slate-900">Recent Visits</div>
          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {(data.recentVisits || []).map((v) => (
              <div key={v.id} className="rounded-xl border p-3">
                <div className="flex items-center justify-between">
                  <div className="font-medium">{v.salesperson_name || v.user_name || "—"}</div>
                  <div className="text-xs text-slate-500">
                    {new Date(v.when || v.visited_at || v.created_at).toLocaleString("en-GB")}
                  </div>
                </div>
                <div className="mt-1 text-sm text-slate-600">{v.remarks || "—"}</div>
              </div>
            ))}
            {(data.recentVisits || []).length === 0 && (
              <div className="rounded-xl border p-3 text-slate-500">No visits</div>
            )}
          </div>
          {/* Desktop table */}
          <table className="hidden md:table min-w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left">Date/Time</th>
                <th className="px-3 py-2 text-left">Rep</th>
                <th className="px-3 py-2 text-left">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {(data.recentVisits || []).map((v) => (
                <tr key={v.id} className="border-t">
                  <td className="px-3 py-2">
                    {new Date(v.when || v.visited_at || v.created_at).toLocaleString("en-GB")}
                  </td>
                  <td className="px-3 py-2">{v.salesperson_name || v.user_name || "—"}</td>
                  <td className="px-3 py-2">{v.remarks || "—"}</td>
                </tr>
              ))}
              {(data.recentVisits || []).length === 0 && (
                <tr><td colSpan={3} className="px-3 py-2 text-slate-500">No visits</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
